/**
 * Reusable Form Field Component with Validation
 * Provides consistent validation UI across all forms
 */
import React, { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export const FormField = ({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  onBlur,
  validation,
  placeholder,
  required = false,
  options = [],
  className = '',
  disabled = false,
  ...props 
}) => {
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (hasBeenTouched && validation) {
      setShowValidation(true);
    }
  }, [hasBeenTouched, validation]);

  const handleBlur = (e) => {
    setHasBeenTouched(true);
    setShowValidation(true);
    if (onBlur) onBlur(e);
  };

  const getValidationClasses = () => {
    if (!showValidation || !validation) {
      return 'border-gray-300 focus:border-blue-500';
    }
    
    return validation.isValid 
      ? 'border-green-500 focus:border-green-500' 
      : 'border-red-500 focus:border-red-500';
  };

  const getValidationIcon = () => {
    if (!showValidation || !validation) return null;
    
    // Only show icon if there's actual content
    if (!value || value.toString().trim() === '') return null;
    
    // if (validation.isValid) {
    //   return <CheckCircle className="h-4 w-4 text-green-500" />;
    // } else {
    //   return <XCircle className="h-4 w-4 text-red-500" />;
    // }
  };

  const renderInput = () => {
    const inputProps = {
      value,
      onChange,
      onBlur: handleBlur,
      placeholder,
      disabled,
      className: `${getValidationClasses()} ${className}`,
      ...props
    };

    switch (type) {
      case 'textarea':
        return <Textarea {...inputProps} />;
      
      case 'select':
        return (
          <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className={`${getValidationClasses()} ${className}`}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'email':
        return <Input type="email" {...inputProps} />;
      
      case 'password':
        return <Input type="password" {...inputProps} />;
      
      case 'tel':
        return <Input type="tel" {...inputProps} />;
      
      case 'date':
        return <Input type="date" {...inputProps} />;
      
      case 'time':
        return <Input type="time" {...inputProps} />;
      
      default:
        return <Input type="text" {...inputProps} />;
    }
  };

  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
        {getValidationIcon()}
      </Label>
      
      {renderInput()}
      
      {showValidation && validation && !validation.isValid && (
        <div className="flex items-center gap-1 text-red-600 text-sm">
          <AlertCircle className="h-3 w-3" />
          <span>{validation.message}</span>
        </div>
      )}
      
     
    </div>
  );
};

export default FormField;
